/**
 * Conversation Recorder Component
 * Hoofdcomponent voor het opnemen en opslaan van gesprekken.
 * Veerkrachtig tegen internet-uitval en telefoongesprekken:
 *  - Opname wordt opgeslagen in IndexedDB vóór upload
 *  - Bij mislukte upload: "Opnieuw proberen"-knop (geen auto-reset)
 *  - Automatische pauze bij achtergrond (telefoon / tab-wissel)
 *  - Herstel-banner bij app-start als er een niet-verzonden opname is
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useMsal } from '@azure/msal-react';
import { useUserInfo } from '../hooks/useUserInfo';
import { useAudioRecorder } from '../hooks/useAudioRecorder';
import { useWakeLock } from '../hooks/useWakeLock';
import { useRecordingStorage, PendingRecording } from '../hooks/useRecordingStorage';
import { conversationService } from '../services/conversationService';
import { wemService, WemClient } from '../services/wemService';
import { ConversationStatus } from '../types/models';
import { apiScopes } from '../config/authConfig';
import { RecordingControls } from './RecordingControls';
import { StatusIndicator } from './StatusIndicator';
import { ConsentToggle } from './ConsentToggle';
import { RecoveryBanner } from './RecoveryBanner';
import './ConversationRecorder.css';

export const ConversationRecorder: React.FC = () => {
  const { instance, accounts } = useMsal();
  const userInfo = useUserInfo();
  const { requestWakeLock, releaseWakeLock } = useWakeLock();
  const { savePendingRecording, deletePendingRecording, loadPendingRecording } =
    useRecordingStorage();

  const [inwoner, setInwoner] = useState('');
  const [typeGesprek, setTypeGesprek] = useState('Keukentafelgesprek');
  const [consentGiven, setConsentGiven] = useState(false);
  const [status, setStatus] = useState<ConversationStatus>(ConversationStatus.Draft);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  // WEM dossiers
  const [wemClients, setWemClients] = useState<WemClient[]>([]);
  const [selectedDossierId, setSelectedDossierId] = useState('');
  const [isLoadingClients, setIsLoadingClients] = useState(false);

  // Veerkracht-state
  const [pendingRecordingId, setPendingRecordingId] = useState<string | null>(null);
  const [pausedByVisibility, setPausedByVisibility] = useState(false);
  const [recoveryRecord, setRecoveryRecord] = useState<PendingRecording | null>(null);

  // Vastleggen van de duur op het moment van stoppen (voorkomt stale state in callback)
  const finalDurationRef = useRef<number>(0);
  const inwonerRef = useRef<string>('');
  const typeGesprekRef = useRef<string>('Keukentafelgesprek');
  const selectedDossierIdRef = useRef<string>('');

  // Sync refs met state zodat de onBlobReady-callback altijd actuele waarden heeft
  useEffect(() => { inwonerRef.current = inwoner; }, [inwoner]);
  useEffect(() => { typeGesprekRef.current = typeGesprek; }, [typeGesprek]);
  useEffect(() => { selectedDossierIdRef.current = selectedDossierId; }, [selectedDossierId]);

  // Callback: wordt aangeroepen door useAudioRecorder zodra de blob klaar is
  const handleBlobReady = useCallback(async (blob: Blob) => {
    try {
      const arrayBuffer = await blob.arrayBuffer();
      const id = await savePendingRecording({
        timestamp: Date.now(),
        audioData: arrayBuffer,
        mimeType: blob.type,
        inwoner: inwonerRef.current.trim(),
        datumGesprek: new Date().toISOString(),
        typeGesprek: typeGesprekRef.current,
        duration: finalDurationRef.current,
        dossierId: selectedDossierIdRef.current || undefined,
      });
      setPendingRecordingId(id);
    } catch (e) {
      // IndexedDB niet beschikbaar (bijv. privé-modus) — geen blokkade voor normale flow
      console.warn('⚠️ Kon opname niet opslaan in IndexedDB:', e);
    }
  }, [savePendingRecording]);

  const {
    isRecording,
    isPaused,
    recordingDuration,
    startRecording,
    pauseRecording,
    resumeRecording,
    stopRecording,
    audioBlob,
    resetRecording,
  } = useAudioRecorder({
    onBlobReady: handleBlobReady,
    onError: (msg) => {
      setError(msg);
      setStatus(ConversationStatus.Failed);
    },
    onPausedByVisibility: () => setPausedByVisibility(true),
  });

  // Bij app-start: controleer of er een niet-verzonden opname in IndexedDB staat
  useEffect(() => {
    loadPendingRecording()
      .then((record) => { if (record) setRecoveryRecord(record); })
      .catch((e) => console.warn('⚠️ Kon IndexedDB niet laden:', e));
  }, []);

  // WEM dossiers laden bij mount
  useEffect(() => {
    if (accounts[0]) loadWemClients();
  }, [accounts]);

  // Upload starten zodra de blob beschikbaar is én status Processing is
  useEffect(() => {
    if (audioBlob && status === ConversationStatus.Processing) {
      handleUpload(audioBlob);
    }
  }, [audioBlob, status]);

  const loadWemClients = async () => {
    setIsLoadingClients(true);
    try {
      const tokenResponse = await instance.acquireTokenSilent({
        scopes: apiScopes.api,
        account: accounts[0],
      });
      const clients = await wemService.getClients(tokenResponse.accessToken);
      setWemClients(clients);
    } catch (error) {
      console.error('❌ Failed to load WEM clients:', error);
    } finally {
      setIsLoadingClients(false);
    }
  };

  const handleStartConversation = async () => {
    if (!inwoner.trim()) { setError('Vul eerst de naam van de inwoner in'); return; }
    if (!consentGiven) { setError('Toestemming voor opname is vereist'); return; }

    setError(null);
    setSuccessMessage(null);
    setPausedByVisibility(false);

    try {
      await startRecording();
      await requestWakeLock();
      setStatus(ConversationStatus.Recording);
    } catch (err: any) {
      setError(err.message || 'Kan opname niet starten');
    }
  };

  const handleStopConversation = async () => {
    // Vastleggen van de huidige duur vóór stopRecording() de state reset
    finalDurationRef.current = recordingDuration;
    setPausedByVisibility(false);
    await stopRecording();
    await releaseWakeLock();
    setStatus(ConversationStatus.Processing);
  };

  const handleUpload = async (blob: Blob) => {
    try {
      setStatus(ConversationStatus.Processing);

      let tokenResponse;
      try {
        tokenResponse = await instance.acquireTokenSilent({
          scopes: apiScopes.api,
          account: accounts[0],
        });
      } catch (_silentError) {
        tokenResponse = await instance.acquireTokenPopup({
          scopes: apiScopes.api,
          account: accounts[0],
        });
      }

      const response = await conversationService.saveConversation(
        {
          inwoner: inwonerRef.current.trim() || inwoner.trim(),
          datumGesprek: new Date().toISOString(),
          audioBlob: blob,
          duration: finalDurationRef.current || recordingDuration,
          typeGesprek: typeGesprekRef.current || typeGesprek,
          dossierId: selectedDossierIdRef.current || selectedDossierId || undefined,
        },
        tokenResponse.accessToken
      );

      if (response.success) {
        setStatus(ConversationStatus.Completed);
        const minutes = Math.floor((finalDurationRef.current || recordingDuration) / 60);
        const seconds = (finalDurationRef.current || recordingDuration) % 60;
        setSuccessMessage(
          `Gesprek succesvol opgeslagen! Duur: ${minutes}:${seconds.toString().padStart(2, '0')}`
        );

        // Verwijder de opname uit IndexedDB na succesvolle upload
        if (pendingRecordingId) {
          await deletePendingRecording(pendingRecordingId).catch(() => {});
          setPendingRecordingId(null);
        }
        setRecoveryRecord(null);

        setTimeout(() => {
          setInwoner('');
          setTypeGesprek('Keukentafelgesprek');
          setConsentGiven(false);
          setSelectedDossierId('');
          setStatus(ConversationStatus.Draft);
          setSuccessMessage(null);
          resetRecording();
        }, 3000);
      } else {
        throw new Error(response.message || 'Upload mislukt');
      }
    } catch (err: any) {
      console.error('💥 Upload error:', err);
      setStatus(ConversationStatus.Failed);
      // Geen auto-reset: gebruiker krijgt retry-knop
      setError(err.message || 'Er ging iets mis bij het verzenden. Probeer opnieuw.');
    }
  };

  // Opnieuw proberen na mislukte upload
  const handleRetry = useCallback(async () => {
    setError(null);

    // Gebruik de in-memory blob als die nog beschikbaar is; anders uit IndexedDB
    let blobToUpload: Blob | null = audioBlob;

    if (!blobToUpload && pendingRecordingId) {
      try {
        const record = await loadPendingRecording();
        if (record) {
          blobToUpload = new Blob([record.audioData], { type: record.mimeType });
          // Herstel de form-state vanuit het opgeslagen record
          inwonerRef.current = record.inwoner;
          typeGesprekRef.current = record.typeGesprek;
          finalDurationRef.current = record.duration;
          selectedDossierIdRef.current = record.dossierId ?? '';
        }
      } catch (e) {
        console.error('Kon opname niet laden uit IndexedDB:', e);
      }
    }

    if (blobToUpload) {
      await handleUpload(blobToUpload);
    } else {
      setError('Opname niet meer beschikbaar. Neem het gesprek opnieuw op.');
      setStatus(ConversationStatus.Draft);
    }
  }, [audioBlob, pendingRecordingId, loadPendingRecording]);

  // Nieuw gesprek starten na mislukte upload (verwijdert pending IDB-record)
  const handleNewConversation = useCallback(async () => {
    if (pendingRecordingId) {
      await deletePendingRecording(pendingRecordingId).catch(() => {});
      setPendingRecordingId(null);
    }
    setStatus(ConversationStatus.Draft);
    setError(null);
    resetRecording();
  }, [pendingRecordingId, deletePendingRecording, resetRecording]);

  // Herstel vanuit IndexedDB-banner
  const handleRecoveryRetry = useCallback(async (record: PendingRecording) => {
    setRecoveryRecord(null);
    setPendingRecordingId(record.id);

    // Herstel form-state vanuit het opgeslagen record
    setInwoner(record.inwoner);
    setTypeGesprek(record.typeGesprek);
    inwonerRef.current = record.inwoner;
    typeGesprekRef.current = record.typeGesprek;
    finalDurationRef.current = record.duration;
    selectedDossierIdRef.current = record.dossierId ?? '';

    const blob = new Blob([record.audioData], { type: record.mimeType });
    await handleUpload(blob);
  }, []);

  const handleRecoveryDiscard = useCallback(async () => {
    if (recoveryRecord) {
      await deletePendingRecording(recoveryRecord.id).catch(() => {});
    }
    setRecoveryRecord(null);
  }, [recoveryRecord, deletePendingRecording]);

  // Hervat opname na automatische pauze (telefoon / tab-wissel)
  const handleResumeAfterVisibility = useCallback(() => {
    resumeRecording();
    setPausedByVisibility(false);
  }, [resumeRecording]);

  const canRetry = status === ConversationStatus.Failed;
  const isProcessing = status === ConversationStatus.Processing;
  const isDraft = status === ConversationStatus.Draft;

  return (
    <div className="conversation-recorder">
      <div className="recorder-card">
        <h2>Nieuw Gesprek</h2>

        <StatusIndicator
          status={status}
          duration={recordingDuration}
          isPaused={isPaused}
        />

        {/* Herstel-banner: niet-verzonden opname gevonden bij app-start */}
        {recoveryRecord && isDraft && (
          <RecoveryBanner
            record={recoveryRecord}
            onRetry={handleRecoveryRetry}
            onDiscard={handleRecoveryDiscard}
          />
        )}

        {/* Automatische pauze door achtergrond (telefoon / tab-wissel) */}
        {pausedByVisibility && (
          <div className="alert alert-warning">
            <span className="alert-icon">📵</span>
            <div className="alert-body">
              <strong>Opname gepauzeerd</strong>
              <span>De app ging naar de achtergrond (telefoon of app-wissel).</span>
              <div className="alert-actions">
                <button
                  className="btn-inline btn-inline-primary"
                  onClick={handleResumeAfterVisibility}
                >
                  Hervat opname
                </button>
                <button
                  className="btn-inline btn-inline-secondary"
                  onClick={() => { handleStopConversation(); setPausedByVisibility(false); }}
                >
                  Stop opname
                </button>
              </div>
            </div>
          </div>
        )}

        {error && (
          <div className="alert alert-error">
            <span className="alert-icon">⚠️</span>
            <span>{error}</span>
          </div>
        )}

        {successMessage && (
          <div className="alert alert-success">
            <span className="alert-icon">✓</span>
            <span>{successMessage}</span>
          </div>
        )}

        {/* WEM Dossier Dropdown */}
        {wemClients.length > 0 && (
          <div className="form-group">
            <label htmlFor="dossier">
              WEM Dossier{' '}
              {selectedDossierId && <span className="optional">(optioneel)</span>}
            </label>
            <select
              id="dossier"
              className="form-input form-select"
              value={selectedDossierId}
              onChange={(e) => setSelectedDossierId(e.target.value)}
              disabled={isRecording || isProcessing || isLoadingClients}
            >
              <option value="">-- Geen dossier gekoppeld --</option>
              {wemClients.map((client) => (
                <option key={client.clientId} value={client.clientId}>
                  {client.name} ({client.clientId})
                </option>
              ))}
            </select>
            <small className="form-hint">
              {isLoadingClients
                ? 'Dossiers laden...'
                : 'Selecteer een dossier om te koppelen aan dit gesprek'}
            </small>
          </div>
        )}

        <div className="form-group">
          <label htmlFor="inwoner">
            Naam inwoner <span className="required">*</span>
          </label>
          <input
            id="inwoner"
            type="text"
            className="form-input"
            value={inwoner}
            onChange={(e) => setInwoner(e.target.value)}
            disabled={isRecording || isProcessing}
            placeholder="Bijv. J. de Vries"
            autoComplete="off"
          />
          <small className="form-hint">Vul de naam in van de inwoner</small>
        </div>

        <div className="form-group" style={{ display: 'none' }}>
          <label htmlFor="typeGesprek">Type gesprek</label>
          <select
            id="typeGesprek"
            className="form-input form-select"
            value={typeGesprek}
            onChange={(e) => setTypeGesprek(e.target.value)}
            disabled={isRecording || isProcessing}
          >
            <option value="Keukentafelgesprek">Keukentafelgesprek</option>
            <option value="Check-up">Check-up</option>
          </select>
        </div>

        {isDraft && (
          <ConsentToggle
            checked={consentGiven}
            onChange={setConsentGiven}
            disabled={false}
          />
        )}

        <RecordingControls
          isRecording={isRecording}
          isPaused={isPaused}
          canStart={inwoner.trim() !== '' && isDraft && consentGiven}
          onStart={handleStartConversation}
          onPause={pauseRecording}
          onResume={resumeRecording}
          onStop={handleStopConversation}
          disabled={isProcessing}
          canRetry={canRetry}
          onRetry={handleRetry}
          onNewConversation={handleNewConversation}
        />

        {userInfo && (
          <div className="user-info">
            <small>Opgenomen door: {userInfo.displayName}</small>
          </div>
        )}
      </div>
    </div>
  );
};
